import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Users, Heart, MessageCircle, Clock, Star, Target, Zap, Calendar, Award } from 'lucide-react';
import { StatsCard, ProgressRing } from './InteractiveComponents';
import EnhancedButton from './EnhancedButton';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Personal Analytics Dashboard
export const PersonalAnalyticsDashboard = ({ userId }) => {
  const [timeRange, setTimeRange] = useState('week');
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [error, setError] = useState('');
  const [showAllTipsModal, setShowAllTipsModal] = useState(false);

  // Fetch analytics data from backend
  const fetchAnalyticsData = async (range = 'week') => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(`${API}/analytics/personal?time_range=${range}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError('Failed to load analytics data');
      
      // Use fallback data if API fails
      setAnalyticsData({
        personal_stats: {
          total_matches: 0,
          mutual_matches: 0,
          total_likes: 0,
          profile_views: 0,
          response_rate: 0,
          conversations: 0,
          messages_sent: 0,
          success_rate: 0
        },
        activity_over_time: [],
        recommendations: []
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData(timeRange);
  }, [timeRange]);

  // Track analytics events
  const trackEvent = async (eventType, eventData = {}) => {
    try {
      await axios.post(`${API}/analytics/track-event`, {
        event_type: eventType,
        event_data: eventData
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  };

  // Track dashboard view
  useEffect(() => {
    trackEvent('dashboard_viewed', { section: 'personal_analytics' });
  }, []);

  const handleTimeRangeChange = (newRange) => {
    setTimeRange(newRange);
    trackEvent('analytics_time_range_changed', { time_range: newRange });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-rose-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dating analytics...</p>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <EnhancedButton onClick={() => fetchAnalyticsData(timeRange)}>
            Retry
          </EnhancedButton>
        </div>
      </div>
    );
  }

  const { personal_stats, activity_over_time, recommendations } = analyticsData;

  // Prepare interaction breakdown data for pie chart
  const interactionBreakdown = [
    { name: 'Matches', value: personal_stats.total_matches || 1, color: '#ec4899' },
    { name: 'Likes Sent', value: personal_stats.total_likes || 1, color: '#f43f5e' },
    { name: 'Messages', value: personal_stats.messages_sent || 1, color: '#10b981' },
    { name: 'Conversations', value: personal_stats.conversations || 1, color: '#3b82f6' }
  ];

  // Generate peak activity times from activity data
  const peakActivityTimes = activity_over_time?.length > 0 ? 
    activity_over_time.map(day => ({
      hour: day.date,
      activity: day.matches + day.likes + day.views
    })) : 
    [
      { hour: '6 AM', activity: 5 },
      { hour: '12 PM', activity: 15 },
      { hour: '6 PM', activity: 35 },
      { hour: '9 PM', activity: 45 },
      { hour: '11 PM', activity: 25 }
    ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-6 animate-fadeInUp">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white animate-slideInLeft">Your Dating Analytics</h2>
            <p className="text-sm sm:text-base text-gray-300 animate-slideInLeft stagger-1">Track your success and optimize your dating strategy</p>
          </div>
          
          <div className="flex items-center space-x-3">
            <select
              value={timeRange}
              onChange={(e) => handleTimeRangeChange(e.target.value)}
              className="px-3 py-2 text-sm sm:text-base bg-white/10 border border-white/20 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 text-white backdrop-blur-md"
            >
              <option value="week" className="bg-slate-800">This Week</option>
              <option value="month" className="bg-slate-800">This Month</option>
              <option value="quarter" className="bg-slate-800">Last 3 Months</option>
              <option value="year" className="bg-slate-800">This Year</option>
            </select>
          </div>
        </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <StatsCard
          icon={<Heart className="w-6 h-6" />}
          label="Total Matches"
          value={personal_stats.total_matches}
          trend={{ direction: 'up', value: 12 }}
          color="rose"
        />
        <StatsCard
          icon={<Users className="w-6 h-6" />}
          label="Profile Views"
          value={personal_stats.profile_views}
          trend={{ direction: 'up', value: 8 }}
          color="blue"
        />
        <StatsCard
          icon={<MessageCircle className="w-6 h-6" />}
          label="Response Rate"
          value={`${personal_stats.response_rate}%`}
          trend={{ direction: 'up', value: 5 }}
          color="green"
        />
        <StatsCard
          icon={<Calendar className="w-6 h-6" />}
          label="Conversations"
          value={personal_stats.conversations}
          trend={{ direction: 'up', value: 15 }}
          color="purple"
        />
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Matches Over Time */}
        <div className="bg-white rounded-xl shadow-lg p-6 animate-scaleIn">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            <span>Activity Over Time</span>
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={activity_over_time?.length > 0 ? activity_over_time : peakActivityTimes}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" stroke="#888" />
              <YAxis stroke="#888" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Line type="monotone" dataKey="matches" stroke="#ec4899" strokeWidth={3} dot={{ r: 6 }} />
              <Line type="monotone" dataKey="likes" stroke="#f43f5e" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="views" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Interaction Breakdown */}
        <div className="bg-white rounded-xl shadow-lg p-6 animate-scaleIn stagger-1">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
            <Target className="w-5 h-5 text-purple-500" />
            <span>Interaction Breakdown</span>
          </h3>
          <div className="flex items-center justify-center">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={interactionBreakdown}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {interactionBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Detailed Analytics */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Peak Activity Times */}
        <div className="bg-white rounded-xl shadow-lg p-6 animate-slideInUp">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
            <Clock className="w-5 h-5 text-orange-500" />
            <span>Peak Activity Times</span>
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={peakActivityTimes}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="hour" stroke="#888" />
              <YAxis stroke="#888" />
              <Tooltip />
              <Bar dataKey="activity" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Success Metrics */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-lg p-6 animate-slideInUp stagger-1">
          <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center space-x-2">
            <Award className="w-5 h-5 text-green-500" />
            <span>Success Metrics</span>
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Match Success Rate</span>
              <div className="flex items-center space-x-2">
                <div className="w-24 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full" 
                    style={{ width: `${Math.min(100, personal_stats.success_rate || 0)}%` }}
                  ></div>
                </div>
                <span className="text-sm font-semibold text-green-600">
                  {personal_stats.success_rate || 0}%
                </span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Response Rate</span>
              <div className="flex items-center space-x-2">
                <div className="w-24 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full" 
                    style={{ width: `${Math.min(100, personal_stats.response_rate || 0)}%` }}
                  ></div>
                </div>
                <span className="text-sm font-semibold text-blue-600">
                  {personal_stats.response_rate || 0}%
                </span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Activity Level</span>
              <div className="flex items-center space-x-2">
                <div className="w-24 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-purple-500 h-2 rounded-full" 
                    style={{ width: `${Math.min(100, (personal_stats.total_likes > 0 ? 85 : 20))}%` }}
                  ></div>
                </div>
                <span className="text-sm font-semibold text-purple-600">
                  {personal_stats.total_likes > 0 ? '85%' : '20%'}
                </span>
              </div>
            </div>
          </div>
          
          <div className="mt-6 pt-4 border-t border-green-200">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 mb-1">
                {personal_stats.success_rate >= 70 ? 'A+' : 
                 personal_stats.success_rate >= 50 ? 'B+' : 
                 personal_stats.success_rate >= 30 ? 'C+' : 'D+'}
              </div>
              <div className="text-sm text-gray-600">Overall Grade</div>
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl shadow-lg p-6 animate-slideInUp stagger-2">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
            <Zap className="w-5 h-5 text-rose-500" />
            <span>AI Recommendations</span>
          </h3>
          
          <div className="space-y-3">
            {recommendations.length > 0 ? (
              recommendations.map((rec, index) => (
                <div key={index} className={`p-3 bg-white rounded-lg border ${
                  rec.priority === 'high' ? 'border-rose-200' : 
                  rec.priority === 'medium' ? 'border-blue-200' : 'border-green-200'
                }`}>
                  <div className="flex items-start space-x-2">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                      rec.priority === 'high' ? 'bg-rose-500' : 
                      rec.priority === 'medium' ? 'bg-blue-500' : 'bg-green-500'
                    }`}></div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{rec.title}</p>
                      <p className="text-xs text-gray-600">{rec.description}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-3 bg-white rounded-lg border border-green-200">
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">You're doing great!</p>
                    <p className="text-xs text-gray-600">Keep up the good work with your dating activity</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="mt-4">
            <EnhancedButton 
              size="sm" 
              variant="outline" 
              className="w-full"
              onClick={() => {
                trackEvent('recommendations_viewed');
                setShowAllTipsModal(true);
              }}
            >
              View All Tips
            </EnhancedButton>
          </div>
        </div>
      </div>

      {/* Goals & Progress */}
      <div className="bg-white rounded-xl shadow-lg p-6 animate-fadeInUp">
        <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center space-x-2">
          <Target className="w-5 h-5 text-purple-500" />
          <span>Weekly Goals</span>
        </h3>
        
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <ProgressRing progress={75} size={100} color="rose" />
            <h4 className="font-semibold text-gray-800 mt-3">Get 5 Matches</h4>
            <p className="text-sm text-gray-600">3 of 5 completed</p>
          </div>
          
          <div className="text-center">
            <ProgressRing progress={60} size={100} color="blue" />
            <h4 className="font-semibold text-gray-800 mt-3">Send 20 Messages</h4>
            <p className="text-sm text-gray-600">12 of 20 completed</p>
          </div>
          
          <div className="text-center">
            <ProgressRing progress={100} size={100} color="green" />
            <h4 className="font-semibold text-gray-800 mt-3">Update Profile</h4>
            <p className="text-sm text-gray-600">✅ Completed</p>
          </div>
        </div>
      </div>
      </div>
      
      {/* All Tips Modal */}
      {showAllTipsModal && (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
          <div className="sticky top-0 bg-gradient-to-r from-rose-500 to-pink-500 p-6 rounded-t-2xl">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Zap className="w-6 h-6" />
                AI Dating Success Tips
              </h2>
              <button
                onClick={() => setShowAllTipsModal(false)}
                className="text-white hover:bg-white/20 rounded-full p-2 transition"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Profile Optimization */}
            <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl p-5 border border-rose-200">
              <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                <div className="w-2 h-2 bg-rose-500 rounded-full"></div>
                Optimize Your Profile
              </h3>
              <div className="space-y-2 text-sm text-gray-700">
                <p className="font-semibold">Why it matters:</p>
                <p>Users with complete profiles get 60% more views and 3x more matches.</p>
                <p className="font-semibold mt-3">Action steps:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Add 4-6 high-quality photos showing different aspects of your life</li>
                  <li>Write a bio that's 150-200 characters (sweet spot for engagement)</li>
                  <li>List at least 5 specific interests</li>
                  <li>Add your job title and education</li>
                  <li>Update your location accurately</li>
                </ul>
              </div>
            </div>

            {/* Response Time */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
              <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                Faster Response Time
              </h3>
              <div className="space-y-2 text-sm text-gray-700">
                <p className="font-semibold">Why it matters:</p>
                <p>Responding within 1 hour increases match success by 25% and keeps conversations alive.</p>
                <p className="font-semibold mt-3">Action steps:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Enable push notifications for new messages</li>
                  <li>Set aside 2-3 times daily to check and respond</li>
                  <li>Use quick replies for common questions</li>
                  <li>If you need time, send a quick "I'll reply soon!" message</li>
                  <li>Aim for responses within 2-3 hours during your active hours</li>
                </ul>
              </div>
            </div>

            {/* Stay Active */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
              <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                Stay More Active
              </h3>
              <div className="space-y-2 text-sm text-gray-700">
                <p className="font-semibold">Why it matters:</p>
                <p>Active users get shown to more potential matches and appear higher in search results.</p>
                <p className="font-semibold mt-3">Action steps:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Log in daily, even for just 5 minutes</li>
                  <li>Send likes during peak hours: 7-9 PM on weekdays, 2-4 PM on weekends</li>
                  <li>Aim for 20-30 likes per week for optimal engagement</li>
                  <li>Complete profile views (don't just quick swipe)</li>
                  <li>Engage with matches through speed dates and chat</li>
                </ul>
              </div>
            </div>

            {/* General Tips */}
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-5 border border-purple-200">
              <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                General Success Tips
              </h3>
              <div className="space-y-2 text-sm text-gray-700">
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Be authentic:</strong> Show your true personality in your profile</li>
                  <li><strong>Quality over quantity:</strong> Focus on meaningful connections</li>
                  <li><strong>Use speed dates:</strong> 3-minute video calls lead to better matches</li>
                  <li><strong>Ask questions:</strong> Show genuine interest in your matches</li>
                  <li><strong>Be patient:</strong> Great connections take time to develop</li>
                  <li><strong>Stay positive:</strong> Maintain a friendly, upbeat tone</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 bg-gray-50 p-4 rounded-b-2xl border-t">
            <EnhancedButton
              onClick={() => setShowAllTipsModal(false)}
              className="w-full"
            >
              Got It!
            </EnhancedButton>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};

// Match Success Analytics
export const MatchSuccessAnalytics = ({ matches = [] }) => {
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [error, setError] = useState('');

  // Fetch match success analytics
  const fetchMatchSuccessData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(`${API}/analytics/match-success`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Error fetching match success analytics:', error);
      setError('Failed to load match success data');
      
      // Use fallback data if API fails
      setAnalyticsData({
        success_metrics: {
          total_matches: 0,
          successful_dates: 0,
          avg_time_to_date: '0 days',
          ghosting_rate: 0,
          response_rate: 0,
          best_matching_times: []
        },
        match_quality_analysis: [],
        success_rate: 0,
        conversation_success_rate: 0
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatchSuccessData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-rose-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading match success analytics...</p>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <EnhancedButton onClick={fetchMatchSuccessData}>
            Retry
          </EnhancedButton>
        </div>
      </div>
    );
  }

  const { success_metrics, match_quality_analysis } = analyticsData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-6 animate-fadeInUp">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white animate-slideInLeft">Match Success Analytics</h2>
            <p className="text-sm sm:text-base text-gray-300 animate-slideInLeft stagger-1">Deep insights into your matching patterns and success rates</p>
          </div>
        </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          icon={<Heart className="w-6 h-6" />}
          label="Success Rate"
          value={`${success_metrics.total_matches > 0 ? Math.round((success_metrics.successful_dates / success_metrics.total_matches) * 100) : 0}%`}
          color="rose"
        />
        <StatsCard
          icon={<Calendar className="w-6 h-6" />}
          label="Avg Time to Date"
          value={success_metrics.avg_time_to_date}
          color="blue"
        />
        <StatsCard
          icon={<MessageCircle className="w-6 h-6" />}
          label="Response Rate"
          value={`${success_metrics.response_rate}%`}
          color="green"
        />
        <StatsCard
          icon={<Users className="w-6 h-6" />}
          label="Active Matches"
          value={success_metrics.total_matches - Math.floor(success_metrics.total_matches * success_metrics.ghosting_rate / 100)}
          color="purple"
        />
      </div>

      {/* Match Quality Analysis */}
      {match_quality_analysis.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Match Quality vs Success</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={match_quality_analysis}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="compatibility" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#ec4899" radius={[4, 4, 0, 0]} />
              <Bar dataKey="success" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Best Matching Times */}
      {success_metrics.best_matching_times.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Your Best Matching Times</h3>
          <div className="grid md:grid-cols-3 gap-4">
            {success_metrics.best_matching_times.map((time, index) => (
              <div key={index} className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-lg p-4 text-center">
                <div className="text-lg font-bold text-rose-600">{time}</div>
                <div className="text-sm text-gray-600">Peak Success Time</div>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default {
  PersonalAnalyticsDashboard,
  MatchSuccessAnalytics
};